// File: src/pages/transporters/TransporterView.jsx
// Notes:
// - Read-only view of a Transporter (no edits, no deletes, no submits)
// - Same visual style as TransporterUpsert (inline CSS only)
// - Params.id is required (non-"0"); page loads transporter and displays values
// - Uses ../import paths, no SCSS, no AgGrid

import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Input,
  Label,
  Button,
  Spinner,
  Form,
} from "reactstrap";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useNavigate, useParams } from "react-router-dom";
import swal from "sweetalert";

// ---- Shared imports (SuperAdmin pages → ../import) ----
import { _Get, _Delete } from "../import/ApiEndPoint/ApiCalling";
import {
  View_TransporterByID,
  Delete_Transporter_List,
} from "../import/ApiEndPoint/Api";

/* --- inline css once --- */
const ensureInlineCss = () => {
  const id = "transporter-view-inline-css";
  if (document.getElementById(id)) return;
  const s = document.createElement("style");
  s.id = id;
  s.textContent = `
    .sa { --fs:14px; --fs-lg:15px; --h:40px; --gap:8px; }
    .sa *{ font-size:var(--fs) !important; }
    .sa h1{ font-size:22px !important; font-weight:600; margin:0 0 6px; }
    .sa label{ font-size:13px !important; font-weight:600; margin-bottom:6px; }
    .sa input:not([type="radio"]):not([type="checkbox"]),
    .sa select,
    .sa textarea{ font-size:var(--fs-lg) !important; height:var(--h);
      padding:8px 10px; border-radius:10px; border:1px solid #d6d8e1; background:#fff; }
    .sa .btn{ border-radius:10px; padding:8px 14px; font-weight:500; }
    .sa .card-soft{ background:#f7f9ff; border:1px solid #e5e7ef; border-radius:12px; padding:12px; box-shadow: rgba(16, 24, 40, .04) 0 1px 2px; }
    .sa .row.g-tight{ row-gap: var(--gap); }
    .sa .myphoneinput{ height:var(--h) !important; font-size:var(--fs-lg) !important; padding:8px 10px !important; width:100% !important; }
    .sa .block{ border:1px solid #e5e7eb; border-radius:12px; padding:12px; background:#fff; }
    .sa .tag{ display:inline-block; background:#eef2ff; border:1px solid #e5e7eb; padding:2px 8px; border-radius:999px; font-size:12px; margin-left:6px; }
    .sa .radio-line{ display:flex; align-items:center; gap:18px; }
    .sa .radio-line label{ display:flex; align-items:center; gap:8px; margin:0; }
  `;
  document.head.appendChild(s);
};

export default function TransporterView() {
  const navigate = useNavigate();
  const Params = useParams();
  ensureInlineCss();

  const [Loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    companyName: "",
    id: "",
    name: "",
    contactNumber: "",
    email: "",
    pincode: "",
    City: "",
    State: "",
    address1: "",
    OpeningBalance: "",
    Type: "",
    status: "",
    gstNumber: "",
  });

  const [serviceArea, setServiceArea] = useState([
    {
      BMName: "",
      contactNumber: "",
      address: "",
      pincode: "",
      city: "",
      state: "",
    },
  ]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await _Get(View_TransporterByID, Params?.id);
        const t = res?.Transporter || {};
        setServiceArea(
          Array.isArray(t?.serviceArea) && t.serviceArea.length
            ? t.serviceArea.map((sa) => ({
                ...sa,
                contactNumber: String(sa?.contactNumber ?? ""),
              }))
            : [
                {
                  BMName: "",
                  contactNumber: "",
                  address: "",
                  pincode: "",
                  city: "",
                  state: "",
                },
              ]
        );

        setForm({
          companyName: t?.companyName || "",
          id: t?.id || "",
          name: t?.name || "",
          contactNumber: String(t?.contactNumber ?? ""),
          email: t?.email || "",
          pincode: t?.pincode || "",
          City: t?.City || t?.city || "",
          State: t?.State || t?.state || "",
          address1: t?.address1 || t?.address || "",
          OpeningBalance: t?.OpeningBalance || "",
          Type: t?.Type || "",
          status: t?.status || "",
          gstNumber: t?.gstNumber || "",
        });
      } catch (e) {
        swal("Error", "Could not load transporter", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [Params?.id]);

  if (Loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "10rem",
        }}
      >
        <Spinner style={{ height: "3rem", width: "3rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const handleDelete = async (id) => {
    // if (!permissions?.Delete) return;
    const choice = await swal("Warning", "Sure You Want to Delete it", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    });
    if (choice === "delete") {
      try {
        await _Delete(Delete_Transporter_List, id);
        navigate(-1);
      } catch (e) {
        swal("Error", "Could not delete transporter", "error");
      }
    }
  };

  return (
    <div className="sa">
      <Card className="card-soft" style={{ padding: 12 }}>
        <Row className="align-items-center mb-2">
          <Col />
          <Col className="text-center">
            <h1>View Transporter</h1>
          </Col>
          <Col
            className="d-flex justify-content-end gap-4"
            lg="4"
            md="5"
            sm="12"
          >
            <Button
              color="secondary"
              onClick={() => navigate(`/app/transporters/create/${Params?.id}`)}
            >
              Edit
            </Button>
            <Button color="secondary" onClick={() => handleDelete(Params?.id)}>
              Delete
            </Button>
            <Button color="secondary" onClick={() => navigate(-1)}>
              Back
            </Button>
          </Col>
        </Row>

        {/* Form wrapper kept for consistent spacing, but there is NO submit */}
        <Form onSubmit={(e) => e.preventDefault()}>
          <Row className="g-tight">
            {/* LEFT: Basic Information (all disabled) */}
            <Col lg="6" md="12">
              <CardBody className="card-soft">
                <h1 className="text-center">Basic Information</h1>
                <Row className="g-tight">
                  <Col lg="6" md="6" sm="12">
                    <Label>Transporter Name</Label>
                    <Input value={form.companyName} readOnly disabled />
                  </Col>
                  <Col lg="6" md="6" sm="12">
                    <Label>Incharge Name</Label>
                    <Input value={form.name} readOnly disabled />
                  </Col>

                  <Col lg="6" md="6" sm="12">
                    <Label>Contact Number</Label>
                    <PhoneInput
                      inputClass="myphoneinput"
                      country="in"
                      countryCodeEditable={false}
                      value={String(form.contactNumber ?? "")}
                      disabled
                    />
                  </Col>

                  <Col lg="6" md="6" sm="12">
                    <Label>Email</Label>
                    <Input type="email" value={form.email} readOnly disabled />
                  </Col>

                  <Col lg="4" md="6" sm="12">
                    <Label>Pincode</Label>
                    <Input type="text" value={form.pincode} readOnly disabled />
                  </Col>
                  <Col lg="4" md="6" sm="12">
                    <Label>City</Label>
                    <Input value={form.City} readOnly disabled />
                  </Col>
                  <Col lg="4" md="6" sm="12">
                    <Label>State</Label>
                    <Input value={form.State} readOnly disabled />
                  </Col>

                  <Col lg="12" md="12" sm="12">
                    <Label>Address</Label>
                    <Input value={form.address1} readOnly disabled />
                  </Col>

                  <Col lg="4" md="6" sm="12">
                    <Label>Opening Balance</Label>
                    <Input
                      type="text"
                      value={String(form.OpeningBalance ?? "")}
                      readOnly
                      disabled
                    />
                  </Col>

                  <Col lg="4" md="6" sm="12">
                    <Label>O/P Balance Type</Label>
                    <Input
                      type="text"
                      value={
                        form.Type ? (form.Type === "debit" ? "Dr" : "Cr") : ""
                      }
                      readOnly
                      disabled
                    />
                  </Col>

                  <Col lg="4" md="12" sm="12">
                    <Label>Status</Label>
                    <div className="radio-line">
                      <label>
                        <Input
                          type="radio"
                          name="status_view"
                          checked={
                            String(form.status).toLowerCase() === "active"
                          }
                          disabled
                          readOnly
                        />
                        <span>Active</span>
                      </label>
                      <label>
                        <Input
                          type="radio"
                          name="status_view"
                          checked={
                            String(form.status).toLowerCase() === "deactive"
                          }
                          disabled
                          readOnly
                        />
                        <span>Deactive</span>
                      </label>
                    </div>
                  </Col>

                  <Col lg="6" md="6" sm="12">
                    <Label>GST Number</Label>
                    <Input value={form.gstNumber} readOnly disabled />
                  </Col>
                </Row>
              </CardBody>
            </Col>

            {/* RIGHT: Service Locations (all disabled; no Add/Remove) */}
            <Col lg="6" md="12">
              <CardBody className="card-soft">
                <h1 className="text-center">
                  Service Locations{" "}
                  <span className="tag">{serviceArea.length}</span>
                </h1>

                {serviceArea.map((s, idx) => (
                  <div key={idx} className="block" style={{ marginBottom: 10 }}>
                    <Row className="g-tight">
                      <Col lg="6" md="6" sm="12">
                        <Label>Branch Manager</Label>
                        <Input value={s.BMName} readOnly disabled />
                      </Col>
                      <Col lg="6" md="6" sm="12">
                        <Label>Contact Number</Label>
                        <PhoneInput
                          inputClass="myphoneinput"
                          country="in"
                          countryCodeEditable={false}
                          value={String(s.contactNumber ?? "")}
                          disabled
                        />
                      </Col>

                      <Col lg="4" md="4" sm="12">
                        <Label>Pincode</Label>
                        <Input value={s.pincode} readOnly disabled />
                      </Col>
                      <Col lg="4" md="4" sm="12">
                        <Label>City</Label>
                        <Input value={s.city} readOnly disabled />
                      </Col>
                      <Col lg="4" md="4" sm="12">
                        <Label>State</Label>
                        <Input value={s.state} readOnly disabled />
                      </Col>

                      <Col lg="12" md="12" sm="12">
                        <Label>Address</Label>
                        <Input value={s.address} readOnly disabled />
                      </Col>
                    </Row>
                  </div>
                ))}
              </CardBody>
            </Col>
          </Row>

          {/* No submit/update buttons on view */}
          <Row className="mt-2">
            <Col className="d-flex justify-content-center">
              <Button color="secondary" onClick={() => navigate(-1)}>
                Back
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
}
